import { VStack } from "@design-system/VStack/VStack";
import { parseUsageRegions } from "./PickerPlaygroundUsage.lib";
import {
    AttachedExample,
    ButtonElementExample,
    CellTriggerExample,
    ComposedExample,
    CustomValueExample,
    FlyoutExample,
    FormikExample,
    InputTriggerExample,
    MultipleExample,
    SearchableExample,
    SelectExample,
    TreeExample,
} from "./PickerPlaygroundUsageExamples";
import examplesSource from "./PickerPlaygroundUsageExamples.tsx?raw";
import {
    PagedComposedExample,
    PagedExample,
} from "./PickerPlaygroundUsagePagedExamples";
import pagedExamplesSource from "./PickerPlaygroundUsagePagedExamples.tsx?raw";
import styles from "./PickerPlaygroundUsage.module.scss";

const regions = {
    ...parseUsageRegions(examplesSource),
    ...parseUsageRegions(pagedExamplesSource),
};

const shapeItems = [
    "Omitted trigger: the default primary md Button, for a short static list.",
    "searchable: a search bar inside the popup on a Button trigger.",
    "allowCustomValue: the typed text is the value. Text triggers only (input or cellInput).",
    'trigger="input": the field is the search. Use filterable, not searchable.',
    'trigger="cellInput": the same field, chromeless in a table cell.',
    'trigger="cell": a chromeless cell button. Optional tooltip via buttonProps.',
    'trigger="attached": a strip on a host box\'s ring; the popup slides up out of attachedTo.',
    "tree: nested items expand in place.",
    "flyout: nested items open cascading levels to the right.",
    "composed: build the popup yourself, or wrap Trigger in Tooltip.",
    "hasMore, onLoadMore, loadMoreStatus: a server-paged list. Search on the server through onInputValueChange with filterable={false}.",
];

const formItems = [
    "Pass a controlled value: null, the item value, an array under multiple, or a string under allowCustomValue.",
    "Commit through onValueChange.",
    "Put ref on Picker.Select so the form library can focus the trigger on error. The Formik card is in Examples.",
];

const overrideItems = [
    "classNames.popup is the primary hatch for popup max-height and max-width.",
    "A table cell sets --picker-trigger-padding-x on the cell trigger so it inherits the cell padding.",
    "Do not copy Button size or padding. Pass a Button element as trigger instead.",
];

const gotchaItems = [
    "Wrap the composed Picker.Trigger in Tooltip. Wrapping the root does nothing, because the root's outermost node is a context provider.",
    "clearable is ignored on icon-only Buttons: they have no slot to overlay.",
    "searchable is a compile error on input and cellInput. Those filter by typing (filterable).",
    "inline has no trigger, so trigger, ref, clearable, and buttonProps are compile errors.",
    "Use Picker.Button for a childless trigger: a plain Button without children trips the a11y lint, which cannot see that the root fills the label.",
    'A failed page (loadMoreStatus="error") shows a "Couldn\'t load more. Try again" row. Pressing it calls onLoadMore; reaching the end of the list again does not. On the stage, turn off Fail next page, then press Try again.',
];

const usageCards = [
    {
        id: "select",
        title: "Select",
        caption:
            "Omitted trigger renders the primary md Button and takes a controlled value.",
        Example: SelectExample,
    },
    {
        id: "searchable",
        title: "Searchable",
        caption:
            "Popup search on a Button trigger, with a clear control and its own placeholder.",
        Example: SearchableExample,
    },
    {
        id: "multiple",
        title: "Multiple",
        caption:
            "renderValue (or selectionSummary mode count) labels the trigger as N selected.",
        Example: MultipleExample,
    },
    {
        id: "customValue",
        title: "Custom value",
        caption:
            "Typed text is the value. renderFooter is the creatable Add query row.",
        Example: CustomValueExample,
    },
    {
        id: "inputTrigger",
        title: "Input trigger",
        caption: "The field is the search. size maps to the Input sizes.",
        Example: InputTriggerExample,
    },
    {
        id: "cellTrigger",
        title: "Cell trigger",
        caption:
            "Chromeless cell button. tooltip lives in buttonProps, not on the root.",
        Example: CellTriggerExample,
    },
    {
        id: "attached",
        title: "Attached",
        caption:
            "A strip on a host box's 2px ring. attachedTo is the box: the popup sits flush on its top edge, as wide as the box.",
        Example: AttachedExample,
    },
    {
        id: "tree",
        title: "Tree",
        caption:
            "Nested items expand in place. parentSelection cascade ticks every leaf.",
        Example: TreeExample,
    },
    {
        id: "flyout",
        title: "Flyout",
        caption:
            "Nested items open a level to the right. searchable is per level.",
        Example: FlyoutExample,
    },
    {
        id: "buttonElement",
        title: "Button element",
        caption:
            "Pass any Button as trigger. chevron hover keeps the slot and fades the glyph in.",
        Example: ButtonElementExample,
    },
    {
        id: "composed",
        title: "Composed",
        caption:
            "Build the anatomy yourself. Wrap Trigger, not the root, in Tooltip.",
        Example: ComposedExample,
    },
    {
        id: "paged",
        title: "Paged",
        caption:
            "useInfiniteQuery with server search. hasMore, onLoadMore, and loadMoreStatus map from hasNextPage, fetchNextPage, isFetchingNextPage, and isFetchNextPageError.",
        Example: PagedExample,
    },
    {
        id: "pagedComposed",
        title: "Paged, composed",
        caption:
            "Place Picker.LoadMore inside Picker.List, after the last row and before Picker.Footer.",
        Example: PagedComposedExample,
    },
    {
        id: "formik",
        title: "Formik",
        caption:
            "Bind value, onValueChange, and onBlur. ref lands on the trigger button; set aria-invalid when the field is invalid.",
        Example: FormikExample,
    },
];

const BulletList = ({ items }: { items: readonly string[] }) => (
    <ul className={styles.bulletList}>
        {items.map((item) => (
            <li key={item}>{item}</li>
        ))}
    </ul>
);

/**
 * Copy-paste usage docs for Picker: which shape to pick, a live card per
 * shape with its source, and the form / override / gotcha contract.
 */
export const PickerPlaygroundUsage = () => (
    <div>
        <section className={styles.section}>
            <h2 className={styles.title}>Pick a shape</h2>
            <ul className={styles.decisionList}>
                {shapeItems.map((item) => (
                    <li key={item}>{item}</li>
                ))}
            </ul>
        </section>
        <section className={styles.section}>
            <h2 className={styles.title}>Examples</h2>
            <div className={styles.exampleGrid}>
                {usageCards.map(({ id, title, caption, Example }) => (
                    <article
                        key={id}
                        className={styles.card}
                        data-usage-region={id}
                    >
                        <VStack gap={12}>
                            <h3 className={styles.cardTitle}>{title}</h3>
                            <p className={styles.caption}>{caption}</p>
                            <div className={styles.live}>
                                <Example />
                            </div>
                            <pre
                                className={styles.snippet}
                                data-usage-snippet={id}
                            >
                                <code>{regions[id] ?? ""}</code>
                            </pre>
                        </VStack>
                    </article>
                ))}
            </div>
        </section>
        <section className={styles.section}>
            <h2 className={styles.title}>Forms</h2>
            <BulletList items={formItems} />
        </section>
        <section className={styles.section}>
            <h2 className={styles.title}>Overrides</h2>
            <BulletList items={overrideItems} />
        </section>
        <section className={styles.section}>
            <h2 className={styles.title}>Gotchas</h2>
            <BulletList items={gotchaItems} />
        </section>
    </div>
);
